import type { OralExamAggregateTimeSlotStateResponse } from '@/common/api/models/oralExamAggregateTimeSlotStateResponse';
import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';
import { OralExamConductPhase } from '@/common/api/models/oralExamConductPhase';
import { OralExamDriver } from '@/common/api/models/oralExamDriver';
import { OralExamMode } from '@/common/api/models/oralExamMode';
import { OralExamPauseReason } from '@/common/api/models/oralExamPauseReason';
import { OralExamReadiness } from '@/common/api/models/oralExamReadiness';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';
import type {
  OralExamLabCohortState,
  OralExamLabOverridesValue,
  OralExamLabTileLookup,
} from '@/common/oralExam/oralExamLabOverrides';

import {
  PATROL_LAB_MAX_STUDENTS,
  type PatrolLabClockMode,
  type PatrolLabQuery,
} from '@/visual-lab/chapters/patrol/parsePatrolLabQuery';
import {
  PATROL_LAB_QUESTION_SHORT,
  resolvePatrolLabQuestionText,
} from '@/visual-lab/chapters/patrol/patrolLabQuestions';
import { PATROL_LAB_STANDINS } from '@/visual-lab/chapters/patrol/patrolLabStandIns';

export const PATROL_LAB_SLOT_ID = 'patrol-lab-slot';
export const PATROL_LAB_HARRY_BOOKING_ID = 'booking-harry';
export const PATROL_LAB_HERMIONE_BOOKING_ID = 'booking-hermione';
export const PATROL_LAB_RON_BOOKING_ID = 'booking-ron';
export const PATROL_LAB_NEVILLE_BOOKING_ID = 'booking-neville';
export const PATROL_LAB_LUNA_BOOKING_ID = 'booking-luna';
export const PATROL_LAB_GINNY_BOOKING_ID = 'booking-ginny';
export const PATROL_LAB_HARRY_STUDENT_ID = 'student-harry';
export const PATROL_LAB_HERMIONE_STUDENT_ID = 'student-hermione';
export const PATROL_LAB_SUPERVISOR_NAME = 'Prof. Severus Snape';
export const PATROL_LAB_QUESTION = PATROL_LAB_QUESTION_SHORT;
export const PATROL_LAB_CAPACITY = 6;

const PATROL_LAB_WALL_ROSTER = [
  {
    bookingId: PATROL_LAB_HARRY_BOOKING_ID,
    studentId: PATROL_LAB_HARRY_STUDENT_ID,
    studentName: 'Harry Potter',
    portrait: 'harry' as const,
  },
  {
    bookingId: PATROL_LAB_HERMIONE_BOOKING_ID,
    studentId: PATROL_LAB_HERMIONE_STUDENT_ID,
    studentName: 'Hermione Granger',
    portrait: 'hermione' as const,
  },
  {
    bookingId: PATROL_LAB_RON_BOOKING_ID,
    studentId: 'student-ron',
    studentName: 'Ron Weasley',
    portrait: 'ron' as const,
  },
  {
    bookingId: PATROL_LAB_NEVILLE_BOOKING_ID,
    studentId: 'student-neville',
    studentName: 'Neville Longbottom',
    portrait: 'harry' as const,
  },
  {
    bookingId: PATROL_LAB_LUNA_BOOKING_ID,
    studentId: 'student-luna',
    studentName: 'Luna Lovegood',
    portrait: 'hermione' as const,
  },
  {
    bookingId: PATROL_LAB_GINNY_BOOKING_ID,
    studentId: 'student-ginny',
    studentName: 'Ginny Weasley',
    portrait: 'ron' as const,
  },
] as const;

export const PATROL_LAB_WALL_BOOKING_IDS = PATROL_LAB_WALL_ROSTER.map(
  (student) => student.bookingId,
);

export type PatrolLabPayloadKind = 'console' | 'conducting' | 'student';

export type PatrolLabFixture = {
  payloadKind: PatrolLabPayloadKind;
  slotId: string;
  overrides: OralExamLabOverridesValue;
  studentRunState: OralExamRunStateResponse;
  studentFirstName: string;
  studentBookingId: string;
  engagedStudentId: string;
  engagedQuestion: OralExamRunStateResponse['currentQuestion'];
  engagedDeadline: string | undefined;
  engagedStartedAt: string | undefined;
  engagedPaused: boolean;
  engagedIds: OralExamRouteIds;
  engagedRunState: OralExamRunStateResponse;
};

type PatrolLabClockSettings = {
  secondsPerQuestion: number;
  questionMarkerStartedAt: string;
  questionDeadline: string;
  clockPaused: boolean;
};

function resolvePatrolLabClockSettings(clock?: PatrolLabClockMode): PatrolLabClockSettings {
  const now = Date.now();
  const questionMarkerStartedAt = new Date(now - 30_000).toISOString();
  if (clock === 'reading') {
    const secondsPerQuestion = 150;
    return {
      secondsPerQuestion,
      questionMarkerStartedAt,
      questionDeadline: new Date(now + (secondsPerQuestion + 60) * 1000).toISOString(),
      clockPaused: false,
    };
  }
  if (clock === 'answering' || clock === 'paused') {
    const secondsPerQuestion = 150;
    return {
      secondsPerQuestion,
      questionMarkerStartedAt,
      questionDeadline: new Date(now + 120_000).toISOString(),
      clockPaused: clock === 'paused',
    };
  }
  return {
    secondsPerQuestion: 120,
    questionMarkerStartedAt,
    questionDeadline: new Date(now + 90_000).toISOString(),
    clockPaused: false,
  };
}

function buildRunState(
  supervisorStatus: OralExamBookingRunSummary['supervisorStatus'],
  paused: boolean,
  questionText: string,
  clockSettings: PatrolLabClockSettings,
  pauseReason?: OralExamPauseReason,
): OralExamRunStateResponse {
  const isPaused = paused || clockSettings.clockPaused;
  return {
    state: 'in_progress',
    sessionPhase: OralExamSessionPhase.in_exam,
    committed: true,
    conductPhase: OralExamConductPhase.in_exam,
    selectedQuestionIds: ['q-lock'],
    draftSelectedQuestionIds: ['q-lock'],
    currentQuestionIndex: 0,
    currentQuestion: {
      questionId: 'q-lock',
      question: questionText,
    },
    secondsPerQuestion: clockSettings.secondsPerQuestion,
    questionCount: 3,
    questionMarkerStartedAt: clockSettings.questionMarkerStartedAt,
    questionDeadline: clockSettings.questionDeadline,
    isPaused,
    pauseReason: isPaused
      ? (pauseReason ?? OralExamPauseReason.lecturer_pause)
      : undefined,
    capacity: PATROL_LAB_CAPACITY,
    mode: OralExamMode.one_to_n,
    supervisorName: PATROL_LAB_SUPERVISOR_NAME,
    supervisorStatus,
    driver: OralExamDriver.examiner_agent,
    etag: `patrol-lab-run-${supervisorStatus}`,
  };
}

function buildBooking(
  bookingId: string,
  studentId: string,
  studentName: string,
  supervisorStatus: OralExamBookingRunSummary['supervisorStatus'],
  paused: boolean,
  questionText: string,
  clockSettings: PatrolLabClockSettings,
  pauseReason?: OralExamPauseReason,
  submitted = false,
): OralExamBookingRunSummary {
  const runState = buildRunState(
    supervisorStatus,
    paused,
    questionText,
    clockSettings,
    pauseReason,
  );
  if (submitted) {
    return {
      bookingId,
      studentId,
      studentName,
      courseId: 'course-patrol-lab',
      moduleId: 'module-patrol-lab',
      taskId: 'task-patrol-lab',
      present: false,
      isAdmitted: true,
      hasRecording: true,
      idVerification: { status: 'approved' },
      supervisorStatus: OralExamSupervisorStatus.watching,
      readiness: OralExamReadiness.completed,
      runState: {
        ...runState,
        state: 'completed',
        committed: true,
        isPaused: false,
        pauseReason: undefined,
      },
    };
  }
  return {
    bookingId,
    studentId,
    studentName,
    courseId: 'course-patrol-lab',
    moduleId: 'module-patrol-lab',
    taskId: 'task-patrol-lab',
    present: true,
    isAdmitted: true,
    hasRecording: true,
    idVerification: { status: 'approved' },
    supervisorStatus,
    readiness: OralExamReadiness.in_exam,
    runState,
  };
}

function resolveStandIn(summary: OralExamLabTileLookup) {
  const row = PATROL_LAB_WALL_ROSTER.find(
    (student) => student.bookingId === summary.bookingId || student.studentId === summary.studentId,
  );
  if (!row) return null;
  return {
    standInCameraUrl: PATROL_LAB_STANDINS[row.portrait],
    standInScreenUrl: PATROL_LAB_STANDINS.slide,
    hasMic: true,
    studentName: row.studentName,
  };
}

/**
 * One actor × stage cell for Patrol Lab. Observe is lecturer-only;
 * Appear/Engage only change Harry. The other five stay Watching so
 * Wall is a 3×2 grid and Observe is a large hero plus regular cards underneath.
 * `students` trims the roster (Harry first) so 1 and 3 student walls can be
 * judged as pixels; actor studentB needs Hermione, so it keeps at least 2.
 */
export function buildPatrolLabFixture(query: PatrolLabQuery): PatrolLabFixture {
  const questionText = resolvePatrolLabQuestionText(query.question);
  const clockSettings = resolvePatrolLabClockSettings(query.clock);
  const minStudents = query.actor === 'studentB' ? 2 : 1;
  const studentCount = Math.max(minStudents, query.students ?? PATROL_LAB_MAX_STUDENTS);
  const roster = PATROL_LAB_WALL_ROSTER.slice(0, studentCount);
  const harryStatus = query.stage === 'engage'
    ? OralExamSupervisorStatus.engaged
    : query.stage === 'appear' || query.stage === 'observe'
      ? OralExamSupervisorStatus.here_with_you
      : OralExamSupervisorStatus.watching;
  const hermioneRaisedHand = query.hand === 'hermione';
  const harryNeedsHelp = query.stage === 'help';
  const harryPaused = query.stage === 'engage' || harryNeedsHelp;
  const submittedTailCount = query.stage === 'wall' && query.submitted
    ? Math.min(query.submitted, roster.length)
    : 0;
  const submittedBookingIds = new Set(
    roster.slice(roster.length - submittedTailCount).map((student) => student.bookingId),
  );

  const bookingRuns = roster.map((student) => {
    if (submittedBookingIds.has(student.bookingId)) {
      return buildBooking(
        student.bookingId,
        student.studentId,
        student.studentName,
        OralExamSupervisorStatus.watching,
        false,
        questionText,
        clockSettings,
        undefined,
        true,
      );
    }
    if (student.bookingId === PATROL_LAB_HARRY_BOOKING_ID) {
      return buildBooking(
        student.bookingId,
        student.studentId,
        student.studentName,
        harryStatus,
        harryPaused,
        questionText,
        clockSettings,
        harryNeedsHelp ? OralExamPauseReason.help_requested : undefined,
      );
    }
    if (student.bookingId === PATROL_LAB_HERMIONE_BOOKING_ID && hermioneRaisedHand) {
      return buildBooking(
        student.bookingId,
        student.studentId,
        student.studentName,
        OralExamSupervisorStatus.watching,
        true,
        questionText,
        clockSettings,
        OralExamPauseReason.help_requested,
      );
    }
    return buildBooking(
      student.bookingId,
      student.studentId,
      student.studentName,
      OralExamSupervisorStatus.watching,
      false,
      questionText,
      clockSettings,
    );
  });
  const harry = bookingRuns[0];
  const hermione = bookingRuns[1] ?? harry;

  const cohort: OralExamAggregateTimeSlotStateResponse = {
    sessionPhase: OralExamSessionPhase.in_exam,
    capacity: PATROL_LAB_CAPACITY,
    inExamEnteredAt: '2026-09-15T10:00:00.000Z',
    admittedBookingIds: bookingRuns.map((run) => run.bookingId),
    etag: 'patrol-lab-etag',
    bookingRuns,
    maxUnsupervisedSeconds: 120,
  };

  const cohortState: OralExamLabCohortState = {
    cohort,
    bookingRuns,
    capacity: PATROL_LAB_CAPACITY,
    isLoading: false,
    etag: cohort.etag ?? null,
    queryKey: ['patrol-lab-cohort', PATROL_LAB_SLOT_ID],
  };

  // Lab-only hint: the zoomed student is whoever carries here_with_you on the wall.
  const observeBookingId = harryStatus === OralExamSupervisorStatus.here_with_you
    ? PATROL_LAB_HARRY_BOOKING_ID
    : null;

  const actorIsHermione = query.actor === 'studentB';
  const actorBooking = actorIsHermione ? hermione : harry;
  const actorLive = !actorIsHermione && (
    query.stage === 'observe' || query.stage === 'appear' || query.stage === 'engage'
  );

  const payloadKind: PatrolLabPayloadKind = query.actor === 'supervisor'
    ? (query.stage === 'engage' ? 'conducting' : 'console')
    : 'student';

  return {
    payloadKind,
    slotId: PATROL_LAB_SLOT_ID,
    overrides: {
      cohortState,
      observeBookingId,
      resolveTileMedia: resolveStandIn,
      supervisorStillImageUrl: PATROL_LAB_STANDINS.snape,
      supervisorLiveImageUrl: actorLive || (
        query.actor === 'supervisor'
        && (query.stage === 'observe' || query.stage === 'appear' || query.stage === 'engage')
      )
        ? PATROL_LAB_STANDINS.snape
        : null,
      selfViewImageUrl: actorIsHermione
        ? PATROL_LAB_STANDINS.hermione
        : PATROL_LAB_STANDINS.harry,
      examinerPresent: true,
    },
    studentRunState: actorBooking.runState,
    studentFirstName: actorIsHermione ? 'Hermione' : 'Harry',
    studentBookingId: actorBooking.bookingId,
    engagedStudentId: PATROL_LAB_HARRY_STUDENT_ID,
    engagedQuestion: harry.runState.currentQuestion,
    engagedDeadline: harry.runState.questionDeadline,
    engagedStartedAt: harry.runState.questionMarkerStartedAt,
    engagedPaused: harryPaused,
    engagedIds: {
      courseId: harry.courseId,
      moduleId: harry.moduleId,
      taskId: harry.taskId,
      bookingId: harry.bookingId,
    },
    engagedRunState: harry.runState,
  };
}
